/**
 * GROWTH-R1 — Customer Mini App recovery card (in-app only).
 * Never shows internal status codes. Priority: unpaid > checkout > cart.
 */
import type { StoreV2Lang } from "./store-v2-locale.js";

export type RecoveryCardView = {
  recovery_id: string;
  recovery_type: "CART" | "CHECKOUT" | "ORDER_PAYMENT" | "PAYMENT_ATTEMPT";
  public_order_number: string | null;
  amount_uah: number | null;
  item_count: number;
  first_image_url: string | null;
  first_title: string | null;
  copy_uk: { title: string; body: string; primary: string; secondary: string };
  cta_primary: "continue_payment" | "open_order" | "continue_checkout" | "view_cart";
};

export function RecoveryCardV2(props: {
  lang: StoreV2Lang;
  card: RecoveryCardView;
  secondaryOrders?: Array<{ public_order_number?: unknown; amount_uah?: unknown }>;
  onPrimary: () => void;
  onSecondary: () => void;
  onOpenOrder?: (stg: string) => void;
}) {
  const uk = props.lang !== "en";
  const c = props.card;
  const title = uk ? c.copy_uk.title : c.copy_uk.title;
  const body = uk ? c.copy_uk.body : c.copy_uk.body;
  const primary = uk ? c.copy_uk.primary : c.copy_uk.primary;
  const secondary = uk ? c.copy_uk.secondary : c.copy_uk.secondary;
  const amount =
    c.amount_uah != null && Number.isFinite(c.amount_uah) ? `${Number(c.amount_uah)} ₴` : null;

  return (
    <section className="sv2-recovery-card" data-testid="sv2-recovery-card" data-type={c.recovery_type}>
      <div className="sv2-recovery-card-row">
        {c.first_image_url ? (
          <img
            className="sv2-recovery-card-img"
            src={c.first_image_url}
            alt=""
            width={56}
            height={56}
            loading="lazy"
          />
        ) : (
          <div className="sv2-recovery-card-img sv2-recovery-card-img--empty" aria-hidden="true" />
        )}
        <div className="sv2-recovery-card-meta">
          <h2 className="sv2-recovery-card-title">{title}</h2>
          <p className="sv2-recovery-card-body">{body}</p>
          <p className="sv2-recovery-card-stats">
            {c.item_count > 0 ? (uk ? `${c.item_count} поз.` : `${c.item_count} items`) : null}
            {c.item_count > 0 && amount ? " · " : null}
            {amount}
            {c.first_title ? ` · ${c.first_title}` : null}
          </p>
        </div>
      </div>
      <div className="sv2-recovery-card-actions">
        <button type="button" className="sv2-btn sv2-btn-primary" data-testid="sv2-recovery-primary" onClick={props.onPrimary}>
          {primary}
        </button>
        <button
          type="button"
          className="sv2-btn sv2-btn-secondary"
          data-testid="sv2-recovery-secondary"
          onClick={props.onSecondary}
        >
          {secondary}
        </button>
      </div>
      {props.secondaryOrders && props.secondaryOrders.length > 0 ? (
        <ul className="sv2-recovery-secondary-list" data-testid="sv2-recovery-secondary-orders">
          {props.secondaryOrders.map((o) => {
            const stg = String(o.public_order_number ?? "");
            if (!stg) return null;
            return (
              <li key={stg}>
                <button type="button" className="sv2-linkish" onClick={() => props.onOpenOrder?.(stg)}>
                  {stg}
                  {o.amount_uah != null ? ` · ${Number(o.amount_uah)} ₴` : ""}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
